import React, { useContext, useState } from 'react';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  Descriptions,
  Row,
  Skeleton,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd';

import AddUserDialog from '@totem/components/services/remoteAccess/auth0role/details/AddUserDialog';
import Auth0RoleDetailsContext from '@totem/components/services/remoteAccess/auth0role/details/Auth0RoleDetailsContext';
import Auth0RoleUsersTable from '@totem/components/services/remoteAccess/auth0role/details/Auth0RoleUsersTable';
import RecordCountBadge from '@totem/components/ui_components/widgets/RecordCountBadge';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import { normalizeId } from '@totem/components/userContext/security/userUtilities';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { SecurityGroupCheckConstraints } from '@totem/types/user';

const { Title } = Typography;

const EMPTY_ID = '000000000000000000000000';

const displayRef = (id?: string, name?: string) => {
  if (!id || id === EMPTY_ID || !name) return '—';
  return name;
};

export const Auth0RoleDetailsExtra = () => {
  const { userProfile } = useContext(UserProfileContext);
  const { role, users, onRefresh } = useContext(Auth0RoleDetailsContext);
  const [showAddUser, setShowAddUser] = useState<boolean>(false);

  const checkConstraints: SecurityGroupCheckConstraints = {
    regionId: normalizeId(role?.hierarchyReference?.region?.id ?? ''),
    buildingId: normalizeId(role?.hierarchyReference?.building?.id ?? ''),
    controlSystemId: normalizeId(
      role?.hierarchyReference?.controlSystem?.id ?? '',
    ),
  };

  const isRemoteAccessAdmin = permissions.remoteAccessAdmin.hasPermissions(
    userProfile,
    checkConstraints,
  );

  return (
    <Space>
      <RecordCountBadge count={users?.length ?? 0} />
      {isRemoteAccessAdmin && (
        <Button icon={<PlusOutlined />} onClick={() => setShowAddUser(true)}>
          Add User
        </Button>
      )}
      <Tooltip title="Refresh">
        <Button icon={<ReloadOutlined />} onClick={onRefresh} />
      </Tooltip>
      <AddUserDialog
        visible={showAddUser}
        onClose={() => setShowAddUser(false)}
      />
    </Space>
  );
};

const Auth0RoleDetailsLayout = () => {
  const { loading, role } = useContext(Auth0RoleDetailsContext);

  if (loading && !role) {
    return <Skeleton active paragraph={{ rows: 4 }} />;
  }

  return (
    <>
      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Descriptions column={1} size="small">
            <Descriptions.Item label="Name">{role?.name}</Descriptions.Item>
            <Descriptions.Item label="Description">
              {role?.role?.description || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={role?.status === 'Active' ? 'success' : 'default'}>
                {role?.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Top Level">
              {role?.isTopLevel ? <Tag color="blue">Yes</Tag> : 'No'}
            </Descriptions.Item>
          </Descriptions>
        </Col>
        <Col xs={24} md={12}>
          <Descriptions column={1} size="small">
            <Descriptions.Item label="Organization">
              {displayRef(
                role?.hierarchyReference?.organization?.id,
                role?.hierarchyReference?.organization?.name,
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Region">
              {displayRef(
                role?.hierarchyReference?.region?.id,
                role?.hierarchyReference?.region?.name,
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Building">
              {displayRef(
                role?.hierarchyReference?.building?.id,
                role?.hierarchyReference?.building?.name,
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Control System">
              {displayRef(
                role?.hierarchyReference?.controlSystem?.id,
                role?.hierarchyReference?.controlSystem?.name,
              )}
            </Descriptions.Item>
          </Descriptions>
        </Col>
      </Row>

      <Title level={5} style={{ marginTop: 24 }}>
        Users
      </Title>
      <Auth0RoleUsersTable />
    </>
  );
};

export default Auth0RoleDetailsLayout;
